import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import PhygitalIcon from "@/components/PhygitalIcon";
import { useUiSurface, slotText } from "@/hooks/useUiSurface";
import { jsPDF } from "jspdf";
import {
  FileText,
  CheckCircle2,
  ArrowRight,
  Shield,
  AlertCircle,
  Loader2,
  Download,
  Eye
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PilotNDAPage() {
  const { portalId } = useParams();
  const navigate = useNavigate();
  const { slotMap: nda } = useUiSurface("surface_nda");
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  
  const [ndaTemplate, setNdaTemplate] = useState(null);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [hasAgreed, setHasAgreed] = useState(false);
  const [signatureName, setSignatureName] = useState("");
  const [wantsCopy, setWantsCopy] = useState(false);
  
  // Fetch NDA template
  useEffect(() => {
    const fetchNDA = async () => {
      try {
        const res = await fetch(`${API}/api/pilot/nda/template`);
        if (!res.ok) throw new Error("Failed to load NDA");
        const data = await res.json();
        setNdaTemplate(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchNDA();
  }, []);
  
  // Handle scroll to detect bottom
  const handleScroll = (e) => {
    const target = e.target;
    const atBottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 50;
    if (atBottom) {
      setHasScrolledToBottom(true);
    }
  };
  
  // Download NDA as PDF with electronic signature
  const handleDownload = (signedName = null, signedDate = null) => {
    if (!ndaTemplate?.content) return;
    
    const doc = new jsPDF();
    
    // Set up document
    doc.setFont("helvetica", "normal");
    
    // Header
    doc.setFontSize(20);
    doc.setFont("helvetica", "bold");
    doc.text("PHYGITAL", 105, 20, { align: "center" });
    
    doc.setFontSize(14);
    doc.text("NON-DISCLOSURE AGREEMENT", 105, 30, { align: "center" });
    
    // Add horizontal line
    doc.setDrawColor(128, 0, 128);
    doc.setLineWidth(0.5);
    doc.line(20, 35, 190, 35);
    
    // Strip HTML tags for plain text
    const plainText = ndaTemplate.content
      .replace(/<h[1-6][^>]*>(.*?)<\/h[1-6]>/gi, '\n$1\n')
      .replace(/<li[^>]*>(.*?)<\/li>/gi, '• $1\n')
      .replace(/<p[^>]*>(.*?)<\/p>/gi, '$1\n')
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<[^>]+>/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/\n\n+/g, '\n\n')
      .trim();
    
    // Add NDA content with text wrapping
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    const lines = doc.splitTextToSize(plainText, 170);
    
    let y = 45;
    const pageHeight = doc.internal.pageSize.height;
    
    for (let i = 0; i < lines.length; i++) {
      if (y > pageHeight - 50) {
        doc.addPage();
        y = 20;
      }
      doc.text(lines[i], 20, y);
      y += 5;
    }
    
    // Add signature section if signed
    if (signedName && signedDate) {
      if (y > pageHeight - 80) {
        doc.addPage();
        y = 30;
      }
      
      y += 15;
      doc.setDrawColor(128, 0, 128);
      doc.line(20, y, 190, y);
      y += 10;
      
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text("ELECTRONIC SIGNATURE", 20, y);
      y += 10;
      
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.text("This document has been electronically signed and is legally binding.", 20, y);
      y += 15;
      
      // Signature
      doc.setFontSize(11);
      doc.text("Signed by:", 20, y);
      doc.setFont("times", "italic");
      doc.setFontSize(14);
      doc.text(signedName, 50, y);
      y += 10;
      
      // Date and timestamp
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.text(`Date: ${signedDate}`, 20, y);
      y += 6;
      doc.text(`Timestamp: ${new Date().toISOString()}`, 20, y);
      y += 6;
      doc.text("Document ID: NDA-" + portalId, 20, y);
      
      // Footer
      y += 15;
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text("This electronically signed document is valid and enforceable under the E-SIGN Act.", 105, y, { align: "center" });
      doc.text("© 2026 Phygital Systems. All rights reserved.", 105, y + 5, { align: "center" });
    }
    
    // Generate filename
    const dateStr = new Date().toISOString().split('T')[0];
    const filename = signedName 
      ? `Phygital_NDA_Signed_${signedName.replace(/\s+/g, '_')}_${dateStr}.pdf`
      : `Phygital_NDA_${dateStr}.pdf`;
    
    doc.save(filename);
  };
  
  // Handle NDA signing
  const handleSign = async () => {
    if (!hasAgreed || !signatureName.trim()) {
      setError("Please agree to the terms and enter your full name to sign.");
      return;
    }
    
    setSubmitting(true);
    setError(null);
    
    try {
      // Get session token if exists
      const sessionToken = localStorage.getItem("session_token");
      
      // Generate PDF and convert to base64
      const signedDate = new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      
      // Generate PDF data
      let pdfData = null;
      try {
        const doc = new jsPDF();
        
        // Add header
        doc.setFontSize(18);
        doc.setFont("helvetica", "bold");
        doc.text("PILOT PARTNER AGREEMENT", 105, 20, { align: "center" });
        
        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.text("phygital Platform", 105, 28, { align: "center" });
        
        // Add NDA content (from admin-controlled template)
        const ndaContent = ndaTemplate?.content || ndaTemplate?.html || ndaTemplate?.body || "";
        const plainText = ndaContent
          .replace(/<h[1-6][^>]*>(.*?)<\/h[1-6]>/gi, '\n$1\n\n')
          .replace(/<li[^>]*>(.*?)<\/li>/gi, '• $1\n')
          .replace(/<p[^>]*>(.*?)<\/p>/gi, '$1\n')
          .replace(/<br\s*\/?>/gi, '\n')
          .replace(/<[^>]+>/g, '')
          .replace(/&nbsp;/g, ' ')
          .replace(/&amp;/g, '&')
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>')
          .replace(/\n\n+/g, '\n\n')
          .trim();
        
        doc.setFontSize(10);
        const lines = doc.splitTextToSize(plainText, 170);
        let y = 45;
        const pageHeight = doc.internal.pageSize.height;
        
        for (let i = 0; i < lines.length; i++) {
          if (y > pageHeight - 50) {
            doc.addPage();
            y = 20;
          }
          doc.text(lines[i], 20, y);
          y += 5;
        }
        
        // Add signature section
        if (y > pageHeight - 80) {
          doc.addPage();
          y = 30;
        }
        
        y += 15;
        doc.setDrawColor(128, 0, 128);
        doc.line(20, y, 190, y);
        y += 10;
        
        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.text("ELECTRONIC SIGNATURE", 20, y);
        y += 10;
        
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        doc.text("This document has been electronically signed and is legally binding.", 20, y);
        y += 15;
        
        doc.text("Signed by:", 20, y);
        doc.setFont("times", "italic");
        doc.setFontSize(14);
        doc.text(signatureName.trim(), 50, y);
        y += 10;
        
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        doc.text(`Date: ${signedDate}`, 20, y);
        y += 6;
        doc.text(`Time: ${new Date().toLocaleTimeString()}`, 20, y);
        
        // Get base64 data
        pdfData = doc.output('datauristring');
      } catch (pdfErr) {
        console.error("PDF generation error:", pdfErr);
      }
      
      const res = await fetch(`${API}/api/pilot/nda/sign`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          portal_id: portalId,
          signature_name: signatureName.trim(),
          session_token: sessionToken,
          wants_copy: wantsCopy,
          pdf_data: pdfData // Send PDF data to server for storage
        })
      });
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || "Failed to sign NDA");
      }
      
      const data = await res.json();
      
      // Store session token
      if (data.session_token) {
        localStorage.setItem("session_token", data.session_token);
      }
      
      // Download copy if requested
      if (wantsCopy) {
        handleDownload(signatureName.trim(), signedDate);
      }
      
      // Redirect to portal
      navigate(data.redirect_url || `/pilot/portal/${portalId}`);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-gray-200 border-t-black rounded-full animate-spin" />
          <p className="text-gray-500 font-mono text-sm">Loading NDA...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="border-b bg-white sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <PhygitalIcon className="w-8 h-8" variant="light" />
            <span className="font-semibold text-xl">phygital</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Shield className="w-4 h-4" />
            <span>Secure Document</span>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-4 mb-8">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5" />
            </div>
            <span className="text-sm font-medium text-green-600">Info Submitted</span>
          </div>
          <div className="w-12 h-0.5 bg-gray-300" />
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-purple-600 text-white flex items-center justify-center">
              <FileText className="w-4 h-4" />
            </div>
            <span className="text-sm font-medium text-purple-600">Sign NDA</span>
          </div>
          <div className="w-12 h-0.5 bg-gray-300" />
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gray-200 text-gray-400 flex items-center justify-center">
              3
            </div>
            <span className="text-sm text-gray-400">Complete Setup</span>
          </div>
        </div>

        {/* Main Card */}
        <Card className="shadow-lg border-0">
          <CardHeader className="border-b bg-gradient-to-r from-purple-50 to-pink-50">
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-purple-600" />
              {ndaTemplate?.name || slotText(nda, "page_title", "Pilot NDA")}
            </CardTitle>
            <p className="text-sm text-gray-600">
              {slotText(nda, "page_subtitle", "Please read and sign the Non-Disclosure Agreement to continue with your pilot setup.")}
            </p>
          </CardHeader>
          
          {/* Why NDA Matters */}
          <div className="p-4 bg-gray-900 text-white border-b">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-sm mb-1">Why This Matters</h3>
                <p className="text-xs text-gray-300 leading-relaxed">
                  Phygital is operating in <span className="text-purple-400 font-medium">stealth mode</span>. 
                  This strategic approach allows us to build and perfect our product with select partners like you 
                  before competitors can see our innovations. The NDA protects both of us: it keeps our 
                  product roadmap confidential while giving you exclusive early access to features that 
                  are not available to the public yet.
                </p>
              </div>
            </div>
          </div>
          <CardContent className="p-0">
            {/* NDA Content */}
            <ScrollArea 
              className="h-[400px] p-6 border-b"
              onScrollCapture={handleScroll}
              data-testid="nda-scroll-area"
            >
              <div 
                className="prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: ndaTemplate?.content || "" }}
              />
            </ScrollArea>
            
            {/* Signature Section */}
            <div className="p-6 space-y-6">
              {!hasScrolledToBottom && (
                <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
                  <AlertCircle className="w-4 h-4" />
                  <span>Please scroll to read the entire document before signing.</span>
                </div>
              )}
              
              {/* Agreement Checkbox */}
              <div className="flex items-start gap-3">
                <Checkbox
                  id="agree"
                  checked={hasAgreed}
                  onCheckedChange={(checked) => setHasAgreed(checked)}
                  disabled={!hasScrolledToBottom}
                  data-testid="nda-agree-checkbox"
                />
                <Label 
                  htmlFor="agree" 
                  className={`text-sm leading-relaxed ${!hasScrolledToBottom ? "text-gray-400" : "text-gray-700"}`}
                >
                  {slotText(nda, "accept_checkbox_label", "I have read, understood, and agree to all terms and conditions of this Non-Disclosure Agreement.")}{" "}
                  I understand that this is a legally binding document.
                </Label>
              </div>
              
              {/* Signature Input */}
              <div className="space-y-2">
                <Label htmlFor="signature" className="text-sm font-medium">
                  Full Legal Name (Digital Signature)
                </Label>
                <Input
                  id="signature"
                  value={signatureName}
                  onChange={(e) => setSignatureName(e.target.value)}
                  placeholder="Type your full name to sign"
                  disabled={!hasAgreed}
                  className="font-serif text-lg h-12"
                  data-testid="nda-signature-input"
                />
                <p className="text-xs text-gray-500">
                  By typing your name above, you agree this constitutes your electronic signature.
                </p>
              </div>
              
              {/* Download Copy Option */}
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <Checkbox
                  id="wantsCopy"
                  checked={wantsCopy}
                  onCheckedChange={(checked) => setWantsCopy(checked)}
                  data-testid="nda-download-checkbox"
                />
                <Label htmlFor="wantsCopy" className="text-sm text-gray-600 flex items-center gap-2">
                  <Download className="w-4 h-4" />
                  Save a copy of this agreement for my records
                </Label>
              </div>
              
              {/* Error Message */}
              {error && (
                <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 p-3 rounded-lg">
                  <AlertCircle className="w-4 h-4" />
                  <span>{error}</span>
                </div>
              )}
              
              {/* Sign Button */}
              <Button
                onClick={handleSign}
                disabled={!hasAgreed || !signatureName.trim() || submitting}
                className="w-full h-12 bg-gradient-to-r from-purple-600 to-pink-600 hover:opacity-90 text-white rounded-full font-medium"
                data-testid="nda-sign-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Signing...
                  </>
                ) : (
                  <>
                    Sign & Continue
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
        
        {/* Security Note */}
        <p className="text-center text-xs text-gray-400 mt-6">
          Your signature and agreement are encrypted and securely stored.
          <br />
          Questions? Contact legal@phygital.systems
        </p>
      </div>
    </div>
  );
}
